import _ from 'lodash';
import Element from './Element';
import State from './State';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ContainableMixin from './mixins/ContainableMixin';
import ClickableMixin from './mixins/ClickableMixin';
import mixin from '../privates/utils/mixin';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';

const validateStateInfo = (compType, stateInfo, stateIndex, stateboxID) => {
    if (!validate({
        compType,
        functionName: 'changeState',
        dataToValidate: _.isNil(stateInfo) ? {} : {stateInfo},
        shouldCheckMandatoryFields: true,
        compName: stateboxID
    })) {
        return false;
    }
    if (_.isObject(stateInfo) && !(stateInfo instanceof State)) {
        reporter.reportSdkError(generateError.error_type({
            functionName: 'changeState',
            propertyName: 'stateInfo',
            wrongValue: stateInfo,
            expectedType: 'state,string'
        }));
        return false;
    }

    if (stateIndex < 0) {
        if (stateIndex instanceof State) {
            reporter.reportSdkError(generateError.error_state_input({
                propertyName: 'stateInfo',
                functionName: 'changeState',
                stateboxID,
                wrongValue: stateInfo}));

        } else {
            reporter.reportSdkError(generateError.error_state_input({
                propertyName: 'stateInfo',
                functionName: 'changeState',
                stateboxID,
                wrongValue: stateInfo
            }));
        }
        return false;
    }

    return true;
};

const computeStateIndex = (RMI, compId, stateInfo) => {
    const children = RMI.getChildren(compId);
    return stateInfo instanceof State ?
        children.indexOf(members._compId.get(stateInfo)) :
        _.findIndex(children, (childId) => RMI.getComp(childId).id === stateInfo);
};

/**
 * @class MultiStateBox
 * @summary MultiStateBoxes are containers for States. A MultiStateBox can show one State at a time.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.PlayableMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.StateBox
 * @viewername wysiwyg.viewer.components.StateStrip
 * @definitionId wysiwyg.viewer.components.StateBox
 * @definitionId wysiwyg.viewer.components.StateStrip
 * @tagname multistatebox
 */
class MultiStateBox extends Element {

    /**
     * @function onChange
     * @syntax
     * function onChange(handler: EventHandler): MultiStateBox
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the multistatebox moves to a new state.
     * @description
     *  A multistatebox moves to a new state programatically via code or in an app.
     *
     *  The event handler set by the `onChange` function is run immediately before
     *  the multistatebox moves to the new state. Therefore, reading the multistatebox's
     *  [`currentState`](#currentState) within
     *  the event handler function returns the state that the multistatebox
     *  is moving away from.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} eventHandler The name of the function or
     *  the function expression to run when the multistatebox moves to a new state.
     * @returns {$w.MultiStateBox} The multistatebox on which the event is now registered.
     * @see [changeState( )](#changeState)
     * @memberof $w.MultiStateBox
     * @eventtype change
     * @eventhandler
     */
    onChange(callback) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onChange',
            dataToValidate: {onChange: callback},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CHANGE, controllerId, callback);
        return this;
    }

    /**
     * @member currentState
     * @label property
     * @syntax
     * get currentState(): State
     * @summary Gets the multistatebox's current state.
     * @type {$w.State}
     * @memberof $w.MultiStateBox
     * @instance
     * @readonly
     */
    get currentState() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        const currentStateIndex = RMI.getState(compId).currentIndex;
        const currentStateCompId = RMI.getChildren(compId)[currentStateIndex];
        const currentStateType = RMI.getType(currentStateCompId);

        return wixSelector.getInstance(RMI, currentStateType, currentStateCompId, controllerId);
    }

    /**
     * @function changeState
     * @syntax
     * function changeState(stateReference: String | State): Promise<State>
     * @summary Change the multistatebox's current state to a specific state or state id.
     * @description
     *  The `changeState()` function returns a Promise that is resolved when
     *  the multistatebox finishes moving from the current state to the state
     *  referred to by `stateReference`.
     *
     *  You can retrieve [`State`]($w.State.html) objects to pass to the
     *  `stateReference` parameter from your multistatebox using the
     *  [`currentState`](#currentState) or [`states`](#states) properties.
     *
     *  If passing a string, note that state ids in a multistatebox are accessible through the
     *  id of a state.
     * @param {external:String|$w.State} stateReference The state to move to. Either the id of the state or a [`State`](#w.State.html) from the MultiStateBox.
     * @returns {external:Promise}
     * @fulfill {$w.State} The state that the multistatebox changed to.
     * @throws {external:Error} If no argument was provided or the argument is not a string or an existing state.
     * @memberof $w.MultiStateBox
     */

    changeState(stateInfo) {
        return new Promise((resolve, reject) => {
            const compType = members._type.get(this).compType;
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const wixSelector = members._wixSelector.get(this);
            const controllerId = members._controllerId.get(this);

            const stateIndex = computeStateIndex(RMI, compId, stateInfo);
            if (!validateStateInfo(compType, stateInfo, stateIndex, compId)) {
                return reject(generateError.error_state_input({
                    propertyName: 'stateInfo',
                    functionName: 'changeState',
                    stateboxID: this.id,
                    wrongValue: stateInfo
                }));
            }

            RMI.executeCompBehavior(compId, 'changeState', {stateIndex}, () => {
                const childrenIds = RMI.getChildren(compId);
                const stateCompID = childrenIds[stateIndex];
                const state = RMI.getComp(stateCompID);
                resolve(wixSelector.getInstance(RMI, state.type, stateCompID, controllerId));
            });
        });
    }

    /**
     * @member states
     * @label property
     * @syntax
       * get states(): State[]
     * @summary Gets a list of all the states contained in the multistatebox.
     * @description
     *  Note that the state indices in a multistatebox are zero-based, even though
     *  they are numbered starting from 1 in the Editor.
     * @type {$w.State[]}
     * @memberof $w.MultiStateBox
     * @instance
     * @readonly
     */
    get states() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        return RMI.getChildren(compId).map((id) => wixSelector.getInstance(RMI, RMI.getComp(id).type, id, controllerId));
    }

    toJSON() {
        const {id, type, global, rendered, currentState} = this;
        return {id, type, global, rendered, currentStateId: currentState.id};
    }
}

mixin(MultiStateBox.prototype, HiddenCollapsedMixin.prototype);
mixin(MultiStateBox.prototype, ClickableMixin.prototype);
mixin(MultiStateBox.prototype, ContainableMixin.prototype);

export default MultiStateBox;
